/**
 * Pure styling primitives for the low-zoom cluster layer.
 *
 * Stream A PR 2 / commit M4 — the cluster layer renders aggregated
 * MapLibre cluster features at zoom 0–6 per ADR-016 Open Item 5
 * MVP default; the styling functions here compose the MapLibre paint
 * expressions + the source-side ``clusterProperties`` aggregation
 * spec so the merge of *count → color → radius* stays pure and
 * unit-testable independent of MapLibre.
 *
 * # Why a separate styling module
 *
 * The cluster layer's :class:`CircleLayerSpecification.paint` is
 * composed from three things:
 *
 *   1. ``circle-color`` keyed on the *dominant status* across the
 *      cluster's members. The hierarchy is **offline > degraded >
 *      healthy > unknown** — operator-visibility (CLAUDE.md §8.1)
 *      mandates the worst-status-wins surfacing so a single offline
 *      Producer never hides inside a "healthy" cluster glyph.
 *   2. ``circle-radius`` keyed on ``point_count`` so denser clusters
 *      visually outweigh small ones. Bands match the storyboard
 *      Frame 1 description ("larger; 24 px diameter").
 *   3. ``circle-stroke-color`` matches the fill but slightly
 *      darker — picks up the design-system status tokens through
 *      :func:`getStatusColor` so Stream B's dark theme participates.
 *
 * # Dominant-status hierarchy as MapLibre case expression
 *
 * MapLibre's data-driven expressions can read aggregated cluster
 * properties when the source is configured with
 * ``clusterProperties``. We aggregate four counters
 * (``count_offline``, ``count_degraded``, ``count_healthy``,
 * ``count_unknown``) and the paint expression resolves dominant by
 * a chained ``case`` evaluating ``count_offline > 0`` first, then
 * ``count_degraded > 0``, then ``count_healthy > 0``. Everything
 * else falls through to unknown.
 *
 * # Cluster count label
 *
 * Rendered via a sibling ``symbol`` layer keyed on
 * ``point_count_abbreviated`` (MapLibre native — emits "1.2k" for
 * 1200) per orchestrator decision 4. The layer config lives in
 * :file:`cluster-layer.tsx`; only the text-color / size hints are
 * exported here.
 *
 * Cross-references:
 *   - REQ-UI-010 (cluster rendering — note: MapLibre native client-
 *     side aggregation; REQ-UI-010 phrases this as "the BFF emits
 *     a cluster event", which is the post-MVP server-side path per
 *     ADR-016 §2 Stage 4; orchestrator decision baked-in 2026-05-13
 *     to use client-side native clustering for MVP; doc-only
 *     follow-up to REQ-UI-010 phrasing tracked by orchestrator).
 *   - REQ-UI-011 (zoom 7+ individual markers — ``clusterMaxZoom: 6``
 *     terminates aggregation at z6 inclusive; z7+ falls through to
 *     :file:`snapshot-layer-impl.ts` markers).
 *   - REQ-UI-040 / REQ-UI-046 (paired light/dark palettes + semantic
 *     distinguishability — colors flow through :func:`getStatusColor`
 *     so Stream B's theme flip propagates).
 *   - ADR-016 §2 Stage 4, §4 (clustering + WebGL).
 *   - ADR-016 Open Item 5 (cluster zoom 0–6 / individual zoom 7+).
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md
 *     §"Cluster behavior".
 *   - docs/engineering/detail/storyboards/shared/design-system.md
 *     §"Semantic / status".
 */

import type { ExpressionSpecification } from "maplibre-gl";
import {
  HEALTH_STATE,
  platformSingleColorOrNull,
  resolveStatusColor,
  type PlatformColorConfig,
} from "@/map/marker-styling";

/**
 * Cluster aggregation max zoom — features at z <= 6 cluster; z > 6
 * resolves to individual markers. Per ADR-016 Open Item 5 MVP
 * default accepted 2026-05-12.
 *
 * Exported so callers (snapshot-layer-impl + the cluster-layer
 * component + tests) share a single source of truth.
 *
 * Cross-references:
 *   - ADR-016 Open Item 5.
 *   - REQ-UI-011.
 */
export const CLUSTER_MAX_ZOOM = 6;

/**
 * Fill opacity for the cluster bubble's ``circle-opacity`` paint key.
 *
 * The cluster bubble is rendered **semi-transparent** (with a very thin same-hue
 * outline, :const:`CLUSTER_STROKE_WIDTH_PX`) so the basemap still reads through
 * it — the operator keeps terrain / coastline context under a dense cluster
 * instead of an opaque disc punching a hole in the map. 0.7 lets enough basemap
 * through to read as
 * translucent while keeping the status colour (and therefore the dominant-
 * status signal, REQ-UI-046) clearly legible on **both** the light and dark
 * basemaps; the count label rides its own white halo
 * (:const:`CLUSTER_LABEL_HALO_COLOR`) so it stays fully readable over the
 * translucent fill (CLAUDE.md §8.1 — operator-visibility).
 *
 * Cross-references:
 *   - REQ-UI-010 (cluster rendering) / REQ-UI-046 (status legibility, both
 *     themes).
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md
 *     §"Cluster behavior".
 */
export const CLUSTER_FILL_OPACITY = 0.7;

/**
 * Stroke width (px) for the cluster bubble's bounding outline.
 *
 * A **very thin** hairline ring in the cluster's own status colour (full opacity,
 * so it reads slightly crisper than the 0.7 translucent fill) gives the bubble a
 * defined edge against the basemap — re-added 2026-06-12 on operator feedback,
 * realising the documented stroke intent (header §3). Deliberately hairline
 * (0.5 px): the earlier 2 px **white** stroke read as a heavy outline that fought
 * the map (2026-06-11 cleanup), so this stays thin and same-hue rather than thick
 * and contrasting.
 *
 * Cross-references:
 *   - REQ-UI-010 / REQ-UI-046 (status legibility, both themes).
 */
export const CLUSTER_STROKE_WIDTH_PX = 0.5;

/**
 * Compute the camera zoom for an explicit **"Select on Map"** force-focus
 * (REQ-UI-096): the operator picked an entity from a list and asked the map
 * to focus it, so the camera must zoom in far enough that the entity
 * **un-clusters** and is individually visible — never leaving it hidden inside
 * a low-zoom cluster bubble.
 *
 * Returns ``max(currentZoom, CLUSTER_MAX_ZOOM + 1)``:
 *
 *   - A **zoomed-out** map (``currentZoom <= CLUSTER_MAX_ZOOM``, where entities
 *     still cluster) zooms in to ``CLUSTER_MAX_ZOOM + 1`` (~7) — the first zoom
 *     at which aggregation terminates (``clusterMaxZoom: 6`` is inclusive) and
 *     the entity paints as an individual glyph the ring can land on.
 *   - An **already-zoomed-in** map (``currentZoom > CLUSTER_MAX_ZOOM``) is
 *     **preserved** — the operator's chosen detail level is kept; the focus
 *     recenters without yanking the zoom out (or pointlessly in).
 *
 * Only the **explicit** Select-on-Map force-focus uses this. The passive
 * ``mapSelected`` pan-if-not-in-view (a map-glyph click / cross-pane select)
 * deliberately does **not** change zoom — yanking the camera scale on a glyph
 * the operator can already see would be disorienting (the same rationale as
 * the force-recenter-vs-pan divergence in
 * :file:`selection/map-focus-store.ts`).
 *
 * Pure + exported for unit isolation (the three force-focus call sites —
 * detection / alert layers + :func:`usePlatformFocusFlyTo` — share this so the
 * zoom target stays consistent across kinds; CLAUDE.md §4.4).
 *
 * Cross-references:
 *   - REQ-UI-096 (cross-pane "Select on Map") / REQ-UI-011 (z7+ individual
 *     markers) / ADR-016 Open Item 5 (cluster z0–6 / individual z7+).
 *   - selection/map-focus-store.ts (the one-shot force-focus signal this
 *     zoom accompanies).
 */
export function focusZoom(currentZoom: number): number {
  return Math.max(currentZoom, CLUSTER_MAX_ZOOM + 1);
}

/**
 * MapLibre cluster radius (in pixels) for the screen-space
 * supercluster — the radius within which separate Producers
 * collapse into a single cluster feature. The storyboard Frame 1
 * describes "~24 px screen-space"; MapLibre's default of 50 px is
 * too generous for desktop monitors at the operator zoom levels.
 *
 * Cross-references:
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md
 *     §"Cluster behavior" (cluster forms at ≥2 markers within ~24 px).
 */
export const CLUSTER_RADIUS_PX = 40;

/**
 * MapLibre source IDs / layer IDs for the cluster layer surface.
 *
 * Exported so :file:`cluster-layer.test.tsx` + the verified-ui-done
 * agent's ``evaluate_script`` probe can target them by name.
 *
 * Cross-references:
 *   - REQ-UI-010.
 */
export const CLUSTER_LAYER_ID = "meridian-cluster-layer";
export const CLUSTER_COUNT_LAYER_ID = "meridian-cluster-count-layer";

/**
 * Names of the per-cluster aggregated property the source
 * accumulates from each member's ``health_state``. MapLibre's
 * ``clusterProperties`` source option runs the expression on every
 * member and aggregates the result; these counts are then read by
 * :func:`clusterColorExpression` to resolve dominant-status.
 *
 * Cross-references:
 *   - MapLibre Style Spec §sources §geojson §clusterProperties.
 */
export const CLUSTER_COUNT_OFFLINE = "count_offline";
export const CLUSTER_COUNT_DEGRADED = "count_degraded";
export const CLUSTER_COUNT_HEALTHY = "count_healthy";
export const CLUSTER_COUNT_UNKNOWN = "count_unknown";

/**
 * Build the MapLibre ``clusterProperties`` aggregation spec for the
 * snapshot GeoJSON source.
 *
 * Each property is a tuple ``["+", <per-feature expr>]`` per
 * MapLibre's accumulator syntax — the operator is applied across
 * members. We compute, for each member feature, whether its
 * ``health_state`` matches one of the SAPIENT enum integers; the
 * result is 1 for matches, 0 otherwise. Summing across members
 * yields the count of members at that status.
 *
 * Returns the inner spec object that gets passed straight to the
 * source definition via spread. Exported as a function (not a
 * constant) so each call returns a fresh object — MapLibre's source
 * config holds onto the reference, and downstream tests that
 * mutate the result wouldn't bleed across cases.
 *
 * Cross-references:
 *   - REQ-UI-010.
 *   - schemas/proto/meridian/v1/data/status.proto §HealthState.
 */
export function clusterProperties(): Record<string, readonly [string, ExpressionSpecification]> {
  return {
    [CLUSTER_COUNT_OFFLINE]: [
      "+",
      ["case", ["==", ["get", "health_state"], HEALTH_STATE.OFFLINE], 1, 0],
    ],
    [CLUSTER_COUNT_DEGRADED]: [
      "+",
      ["case", ["==", ["get", "health_state"], HEALTH_STATE.DEGRADED], 1, 0],
    ],
    [CLUSTER_COUNT_HEALTHY]: [
      "+",
      ["case", ["==", ["get", "health_state"], HEALTH_STATE.HEALTHY], 1, 0],
    ],
    [CLUSTER_COUNT_UNKNOWN]: [
      "+",
      [
        "case",
        // UNSPECIFIED (0) + UNKNOWN (4) both surface as the "unknown"
        // marker category per :func:`healthStateToMarkerStatus`; the
        // cluster counter mirrors that so dominant-status precedence
        // stays consistent with individual-marker styling.
        [
          "any",
          ["==", ["get", "health_state"], HEALTH_STATE.UNSPECIFIED],
          ["==", ["get", "health_state"], HEALTH_STATE.UNKNOWN],
        ],
        1,
        0,
      ],
    ],
  };
}

/**
 * Dominant-status hierarchy categories used by :func:`dominantStatus`.
 *
 * Lower-cased categorical names match :file:`marker-styling.ts`'s
 * :type:`MarkerStatus` (minus ``"stale"`` which is a UI-derived
 * overlay not a cluster-eligible aggregate).
 */
export type ClusterDominantStatus = "unhealthy" | "degraded" | "healthy" | "unknown";

/**
 * Per-status counts on a cluster feature. The keys mirror
 * :const:`CLUSTER_COUNT_*` so tests can build the input object from
 * either the constants or literal strings.
 */
export interface ClusterCounts {
  count_offline: number;
  count_degraded: number;
  count_healthy: number;
  count_unknown: number;
}

/**
 * Compute the dominant status across a cluster's member counts.
 *
 * Hierarchy (worst-status-wins per CLAUDE.md §8.1
 * operator-visibility):
 *
 *   1. offline (unhealthy) — any member offline → cluster offline.
 *   2. degraded — any member degraded (and no member offline) →
 *      cluster degraded.
 *   3. healthy — any member healthy (and no member degraded or
 *      offline) → cluster healthy.
 *   4. unknown — all members unknown / unspecified.
 *
 * The hierarchy applies even when the offline member is one of
 * many healthy ones: a single failure surfaces. The empty / no-data
 * case returns ``"unknown"`` so an unpopulated cluster paints grey
 * rather than silently picking a default.
 *
 * Exported as a pure function for unit-test isolation. The MapLibre
 * paint expression in :func:`clusterColorExpression` encodes the
 * same hierarchy at the GL level; this function exists so the
 * hierarchy is testable independent of MapLibre.
 *
 * Cross-references:
 *   - REQ-UI-010.
 *   - REQ-UI-046.
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md
 *     §"Cluster behavior" — "if any contained platform is offline,
 *     the cluster shows offline".
 *   - CLAUDE.md §8.1 — operator-visibility; do not hide a failure.
 */
export function dominantStatus(counts: ClusterCounts): ClusterDominantStatus {
  if (counts.count_offline > 0) {
    return "unhealthy";
  }
  if (counts.count_degraded > 0) {
    return "degraded";
  }
  if (counts.count_healthy > 0) {
    return "healthy";
  }
  return "unknown";
}

/**
 * Compose the MapLibre ``case`` expression for the cluster's
 * ``circle-color`` paint key.
 *
 * Evaluates left-to-right; the first matching branch wins. The
 * branch order encodes the dominant-status hierarchy
 * (:func:`dominantStatus`). Each branch reads the aggregated
 * cluster-property counter against zero — a non-zero count for a
 * higher-priority status overrides any lower-priority count.
 *
 * Color resolution flows through :func:`getStatusColor` so the
 * runtime CSS-variable read in :file:`marker-styling.ts` Stream B
 * theme-flip path applies here too.
 *
 * # Profile single-colour mode (inc6b — REQ-UI-086)
 *
 * When the active Profile's ``map.platform.color_mode`` is ``"single"``
 * (``platformColor.mode``), the cluster bubble — like the individual markers —
 * collapses to the flat Profile ``single_color``
 * (:func:`marker-styling.platformSingleColorOrNull`), so a single-colour fleet
 * reads uniformly at every zoom. The dominant-status ``case`` is the default
 * (``by_status``) behaviour, unchanged.
 *
 * # Per-status overrides (inc-items 4/5 — REQ-UI-086)
 *
 * Each dominant-status colour resolves via
 * :func:`marker-styling.resolveStatusColor`, so a ``map.platform.status_colors``
 * override (the by-status legend picker) recolours the cluster bubble to match
 * the individual markers — an override that applied to markers but not clusters
 * would be an operator-visible inconsistency (CLAUDE.md §8.1). The cluster layer
 * already receives ``platformColor`` (Map.tsx), so the override map rides along.
 *
 * Cross-references:
 *   - REQ-UI-010.
 *   - REQ-UI-040 / REQ-UI-046 / REQ-UI-086.
 *   - docs/engineering/detail/storyboards/shared/design-system.md
 *     §"Semantic / status".
 */
export function clusterColorExpression(
  platformColor?: PlatformColorConfig,
): ExpressionSpecification {
  // Profile ``single`` mode → flat colour for every cluster bubble (REQ-UI-086),
  // matching the individual-marker treatment in :func:`composeColorExpression`.
  const single = platformSingleColorOrNull(platformColor);
  if (single !== null) {
    return single as unknown as ExpressionSpecification;
  }

  // Override-first (``status_colors``), else the live ``--status-*`` token —
  // identical resolution to the individual markers (:func:`composeColorExpression`).
  const overrides = platformColor?.statusColors;
  const unhealthyColor = resolveStatusColor("unhealthy", overrides);
  const degradedColor = resolveStatusColor("degraded", overrides);
  const healthyColor = resolveStatusColor("healthy", overrides);
  const unknownColor = resolveStatusColor("unknown", overrides);

  return [
    "case",
    [">", ["get", CLUSTER_COUNT_OFFLINE], 0],
    unhealthyColor,
    [">", ["get", CLUSTER_COUNT_DEGRADED], 0],
    degradedColor,
    [">", ["get", CLUSTER_COUNT_HEALTHY], 0],
    healthyColor,
    unknownColor,
  ];
}

/**
 * Cluster radius bands (in pixels) by ``point_count``.
 *
 * Storyboard Frame 1 describes the cluster as "24 px diameter"
 * (radius 12 px) for a 4-member cluster. The bands here scale up
 * gently from there so dense clusters get visually heavier without
 * overwhelming the underlying tiles.
 *
 * Format matches MapLibre's ``step`` expression:
 * ``[base_radius, threshold1, radius1, threshold2, radius2, ...]``.
 *
 * Cross-references:
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md
 *     §"Frame 1" (24 px diameter for a 4-member cluster).
 */
export const CLUSTER_RADIUS_STOPS: ReadonlyArray<readonly [number, number]> = [
  // [point_count_threshold, radius_px]. The first entry is the base
  // radius for counts 2..threshold-1.
  [0, 12],
  [10, 16],
  [50, 20],
  [250, 24],
  [1000, 28],
];

/**
 * Compose the MapLibre ``step`` expression for the cluster's
 * ``circle-radius`` paint key keyed on ``point_count``.
 *
 * MapLibre's ``step`` expression takes a base value + alternating
 * threshold / value pairs — for a given ``point_count`` it picks
 * the largest threshold ≤ count and returns the associated value.
 *
 * The first entry of :const:`CLUSTER_RADIUS_STOPS` is treated as the
 * base; the rest become threshold-value pairs.
 *
 * Cross-references:
 *   - REQ-UI-010.
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md.
 */
export function clusterRadiusExpression(): ExpressionSpecification {
  const [base, ...rest] = CLUSTER_RADIUS_STOPS;
  if (base === undefined) {
    // Defensive — :const:`CLUSTER_RADIUS_STOPS` is non-empty by
    // construction. If a future edit accidentally empties it, fall
    // back to a degenerate ``literal``-wrapped 12 px so MapLibre
    // still parses the expression rather than crashing on a bare
    // number where an expression is expected.
    return ["literal", 12] as unknown as ExpressionSpecification;
  }
  const stops = rest.flatMap(([threshold, radius]) => [threshold, radius]);
  return ["step", ["get", "point_count"], base[1], ...stops];
}

/**
 * Cluster text color — the count label rendered on top of the
 * cluster circle.
 *
 * Dark ink (``#0f1117``, == ``--bg-base`` dark per the
 * 2026-05-26 design-system amendment) chosen so the label
 * maintains WCAG AA contrast across every status hex the cluster
 * paints in (Appaloosa-aligned palette): healthy ``#3ecf8e``
 * (~5:1), degraded ``#f5a623`` (~9:1), unhealthy ``#e5534b``
 * (~6:1), unknown ``#8b92aa`` (~4:1), stale ``#4f566b`` (~3:1).
 * Pre-2026-05-26 white-on-status from the original Flow 03
 * frames fails AA on amber (degraded), which is the most
 * frequent non-healthy state — dark ink keeps the count
 * readable on every theme + every status.
 *
 * Matches the Appaloosa convention of dark text on the brand
 * amber (e.g., the ``.dh-drag-badge`` pattern in the sibling
 * project's ``dockview-theme-appaloosa.css`` companion).
 *
 * Cross-references:
 *   - docs/engineering/detail/storyboards/shared/design-system.md
 *     2026-05-26 amendment §"Color" (production palette).
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md
 *     §"Frame 1" (count rendered inside the cluster circle —
 *     storyboard says white; production swaps to dark ink for
 *     WCAG AA across the new palette).
 *   - REQ-UI-046 (status palette retains semantic meaning; text
 *     on status backgrounds meets contrast).
 */
export const CLUSTER_LABEL_COLOR = "#0f1117";

/**
 * Cluster text halo color — a white outline drawn around the dark
 * count glyph.
 *
 * The dark ink (:const:`CLUSTER_LABEL_COLOR`) clears WCAG AA on every
 * status fill on its own, but the 2026-05-28 visual verification
 * (issue #205) found the count still read faintly — a thin dark
 * numeral on a mid-tone green fill is easy to lose. A white halo
 * separates the glyph from *any* status fill (green / amber / red /
 * grey) without disturbing the AA-motivated dark-ink decision, and it
 * picks up the same white as the cluster circle's 2 px stroke so the
 * label and ring read as one chip.
 *
 * Cross-references:
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md
 *     §"Frame 1" (count rendered inside the cluster circle).
 *   - REQ-UI-046 (legibility on status backgrounds).
 */
export const CLUSTER_LABEL_HALO_COLOR = "#ffffff";

/**
 * Cluster text halo width in pixels (MapLibre ``text-halo-width``).
 * 1.5 px is enough to ring a 14 px numeral without bleeding the
 * counts of adjacent clusters together.
 */
export const CLUSTER_LABEL_HALO_WIDTH_PX = 1.5;

/**
 * Cluster text size in pixels. Sized so a 2–3 digit count reads
 * clearly inside the 24 px-diameter base circle. Bumped from 12 → 14
 * with the 2026-05-28 legibility fix (issue #205) — 12 px rendered
 * too faint at the cluster scale.
 *
 * Cross-references:
 *   - docs/engineering/detail/storyboards/shared/design-system.md
 *     §"Type scale" (--text-sm at 13 px, --text-base at 14 px).
 */
export const CLUSTER_LABEL_SIZE_PX = 14;

/**
 * Format a one-line human-readable summary of the cluster recompute
 * for the ``aria-live="polite"`` announcement region.
 *
 * Storyboard Flow 03 §"Accessibility" calls for cluster ARIA labels
 * on a per-cluster basis; the orchestrator's M4 decision 5 broadens
 * that to a *region-summary* announced on viewport recompute, with
 * per-cluster ARIA labels left for the future popup surface
 * (out-of-scope for M4 — Flow 03 hover-popup is Phase 4 polish).
 *
 * Returns a string like "12 clusters visible covering 248
 * producers" suitable for direct assignment to the live region's
 * ``textContent``. Falls through to a "0 clusters" message when the
 * counts are empty.
 *
 * Cross-references:
 *   - REQ-UI-010.
 *   - REQ-UI-059 (keyboard / a11y posture).
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md
 *     §"Accessibility".
 *   - CLAUDE.md §8.1 (operator-visibility — announce what the
 *     system did).
 */
export function clusterSummaryMessage(args: {
  clusterCount: number;
  producerCount: number;
}): string {
  if (args.clusterCount === 0) {
    if (args.producerCount === 0) {
      return "No producers visible.";
    }
    return `${args.producerCount} producer${args.producerCount === 1 ? "" : "s"} visible.`;
  }
  return `${args.clusterCount} cluster${
    args.clusterCount === 1 ? "" : "s"
  } visible covering ${args.producerCount} producer${args.producerCount === 1 ? "" : "s"}.`;
}
